import { ShareButton } from '../../sharing/Sharing';
import { SupportRef } from '../../support/SupportRef';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { deltaText, describeSchedule, moveText, describeWindowRule, refreshedClaim, reviewGrounding, reviewSections, reviewStage, sectionTitle } from '../../stores/slices/reviewsSlice';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import ClaimValue from './ClaimValue';
import TraceLink, { traceLinksOn, traceUrl } from '../agent-common/TraceLink';
import ReviewCreate from './ReviewCreate';
import ReviewWindowRulePicker from './ReviewWindowRulePicker';
import Reconcile from './Reconcile';
import ReviewSectionRow, { WhyChip, openSection, opensAs } from './ReviewSectionRow';
import type { ReviewSchedule, ReviewSection, ReviewWindowRule } from '../../types';
import { StatusChip } from '../../design/primitives';
import { listAnnotations, type Annotation } from '../../apis';
import { claimKey } from './claims';
import TierChip from './TierChip';
import DeckCheck from './DeckCheck';
import ReviewTile from './ReviewTile';
import AskThisReview from './AskThisReview';
import { currentResult, deckName, nextRefresh, sectionForDeckRow, sinceLastMonday, tileGroups, utcStamp } from './reviewPage';
import { sectionClaimKeys } from '../../stores/slices/reviewsSlice';
import { useNumberPopover } from './NumberPopover';

const WEEKDAYS = [['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']] as const;

/**
 * The finding to surface for a pinned number at the review's top level: the
 * analyst's own note (what a walk's "Add to review commentary" writes onto the
 * section), else — only when the move is significant — its walk verdict. A
 * within-normal metric with no note contributes nothing, so the review reads as
 * "what moved and why", not a wall of "nothing to explain".
 */
function findingOf(s: ReviewSection): string {
  const note = (s.commentary || '').trim();
  if (note) return note;
  if (s.flag === 'significant') return (s.why?.verdict?.headline || '').trim();
  return '';
}

const Section = ({ title, hint, children, testId }: { title: string; hint?: string; children: React.ReactNode; testId: string }) => (
  <Box data-testid={testId} sx={{ display: 'grid', gap: 1 }}>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      {hint && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{hint}</Typography>}
    </Box>
    {children}
  </Box>
);

/** The most keys one annotations read takes (the server's CLAIM_LINEAGE_MAX, core/claim_identity.py). */
export const NOTE_KEYS_PER_READ = 28;

/**
 * Notes left on each tile's number (#568): the scorecard's key lineages read
 * in requests of at most NOTE_KEYS_PER_READ keys — a lineage grows a key with
 * every refresh — and counted per tile. A read that fails shows no badge —
 * never a count it did not read.
 */
function useTileNotes(tiles: ReviewSection[], enabled: boolean): Record<string, number> {
  const lineage = useMemo(() => tiles.map((s) => ({ id: s.id, keys: s.claim?.grounding ? sectionClaimKeys(s, claimKey(refreshedClaim(s))) : [] })), [tiles]);
  const all = useMemo(() => Array.from(new Set(lineage.flatMap((l) => l.keys))).sort(), [lineage]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const signature = all.join('\n');
  useEffect(() => {
    if (!enabled || !all.length) { setCounts({}); return undefined; }
    let live = true;
    const reads: Promise<Annotation[]>[] = [];
    for (let i = 0; i < all.length; i += NOTE_KEYS_PER_READ) {
      reads.push(listAnnotations({ kind: 'note', source_ref: all.slice(i, i + NOTE_KEYS_PER_READ), limit: 500 }));
    }
    Promise.all(reads).then((pages) => pages.flat()).then((notes) => {
      if (!live) return;
      const next: Record<string, number> = {};
      for (const l of lineage) next[l.id] = (notes || []).filter((n) => n.source_ref && l.keys.includes(n.source_ref)).length;
      setCounts(next);
    }).catch(() => { if (live) setCounts({}); });
    return () => { live = false; };
  }, [enabled, signature]);  // eslint-disable-line react-hooks/exhaustive-deps
  return counts;
}

/** "Send to #exec": the review's summary to a Slack channel — not yet; it says so rather than pretending (#568). */
function SendToExec() {
  return (
    <Tooltip title="Posting a review to a Slack channel comes with the Slack connector — use Share for now">
      <span><Button size="small" variant="contained" disabled data-testid="review-send-exec">Send to #exec</Button></span>
    </Tooltip>
  );
}

/**
 * A review document (#189, #317): one page, in one order — the title line,
 * the provenance line, the KPI tiles, what moved and why, the segment table,
 * the commentary, the pinned analyses — and a rail. Draft and live are this
 * one component; only the stage and the schedule differ. Every number is a
 * `ClaimValue`, so Why / Pin / Annotate work on the review exactly as in a
 * thread; a significant mover carries the walk's flag and a Why chip that
 * opens the walk as its own thread. Nothing expands inside the page: a tile's
 * details open in the side panel, the schedule in a popover, so the document
 * never reflows under the reader.
 */
export default function ReviewDoc({ threadId }: { threadId: string }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const savedThreads = useVoyagerStore((s) => s.savedThreads);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const starThread = useVoyagerStore((s) => s.starThread);
  const closeThread = useVoyagerStore((s) => s.closeThread);
  const setCommentary = useVoyagerStore((s) => s.setReviewCommentary);
  const schedule = useVoyagerStore((s) => s.scheduleReview);
  const saveWindowRule = useVoyagerStore((s) => s.setReviewWindowRule);
  const [ruleAnchor, setRuleAnchor] = useState<HTMLElement | null>(null);
  const [draftRule, setDraftRule] = useState<ReviewWindowRule | null>(null);
  const refresh = useVoyagerStore((s) => s.refreshReview);
  const busy = useVoyagerStore((s) => s.reviewsBusy[threadId]);
  const error = useVoyagerStore((s) => s.reviewsError);
  const reconciling = useVoyagerStore((s) => !!s.reconcile[threadId]);
  const popover = useNumberPopover();
  // the schedule is a popover on its button, never a form that pushes the page down
  const [scheduleAnchor, setScheduleAnchor] = useState<HTMLElement | null>(null);
  // Rename in place: the title is the review's name on every tab and index; the same atomic rename History uses.
  const renameThread = useVoyagerStore((s) => s.renameThread);
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const commitRename = () => {
    setRenaming(false);
    const next = draftTitle.trim();
    if (next && thread && next !== thread.title) renameThread(thread.id, next);
  };
  // The form starts from the schedule the review has — Save without a
  // change keeps it — and Monday 06:10 only for a review that has none.
  const saved = thread?.doc?.schedule || null;
  const [weekday, setWeekday] = useState<string>(saved?.weekday || 'mon');
  const [at, setAt] = useState<string>(saved?.at || '06:10');
  useEffect(() => { setWeekday(saved?.weekday || 'mon'); setAt(saved?.at || '06:10'); }, [saved?.weekday, saved?.at]);
  const [commentary, setCommentaryText] = useState<string>(thread?.doc?.commentary || '');
  useEffect(() => { setCommentaryText(thread?.doc?.commentary || ''); }, [thread?.doc?.commentary]);

  const stage = reviewStage(thread);
  const sections = useMemo(() => reviewSections(thread), [thread]);
  const scorecard = useMemo(() => sections.find((s) => s.kind === 'template' && s.type === 'kpi_scorecard'), [sections]);
  const scalarTiles = useMemo(() => (scorecard ? sections.filter((s) => s.kind === 'claim' && s.slot === scorecard.id) : []), [sections, scorecard]);
  const notes = useTileNotes(scalarTiles, stage !== 'new' && !reconciling);
  if (!thread) return null;
  // A grading in progress is the screen until it is built or left (#190) —
  // also after a build that started the review but could not pin everything,
  // so "build again" stays reachable. Unstarted otherwise: the one setup.
  if (reconciling) return <Reconcile threadId={threadId} />;
  if (stage === 'new') return <ReviewCreate threadId={threadId} />;

  const doc = thread.doc || {};
  const templateOf = (type: string, style?: string) =>
    sections.find((s) => s.kind === 'template' && s.type === type && (style ? s.narrative_style === style : true));
  const kpiSlot = templateOf('kpi_scorecard');
  const tableSlot = templateOf('table');
  const actionsSlot = templateOf('narrative', 'action_items');
  const commentarySlot = sections.find((s) => s.kind === 'template' && s.type === 'narrative' && s.narrative_style !== 'action_items');
  const pinned = sections.filter((s) => s.kind !== 'template');
  const inSlot = (slot?: ReviewSection) => (slot ? pinned.filter((s) => s.slot === slot.id) : []);
  const tiles = inSlot(kpiSlot).filter((s) => s.kind === 'claim');
  const rows = inSlot(tableSlot).filter((s) => s.kind === 'claim');
  // Every pinned number whose analysis says something — an analyst's note or a significant move — read at
  // the review's top level so nobody has to open a tile to learn what the week's story is.
  const findings = pinned.filter((s) => s.kind === 'claim' && findingOf(s));
  const actions = pinned.filter((s) => s.kind === 'suggestion');
  const others = pinned.filter((s) => s.kind === 'claim' && !tiles.includes(s));
  const perspectiveId = doc.perspective_id || thread.perspectiveId || null;
  const grounding = reviewGrounding(thread);
  const nameOf = (id: string) => perspectives.find((p) => p.id === id)?.name || id;
  // One perspective, or the set an artifact resolved to (#190): the first named, the rest counted.
  const perspectiveName = grounding.length > 1
    ? `${nameOf(grounding[0])} + ${grounding.length - 1} more`
    : (perspectiveId ? nameOf(perspectiveId) : '?');
  const scheduleText = (doc.schedule ? `refreshes ${describeSchedule(doc.schedule)}` : 'not scheduled')
    + (doc.source ? ` · connected to ${doc.source.name || doc.source.workbook || 'a report'} (Tableau)${doc.source.refreshed_at ? `, last refreshed ${String(doc.source.refreshed_at).replace('T', ' ').slice(0, 16)} UTC` : ''}` : '');
  const refreshedText = doc.last_refreshed_at ? ` · refreshed ${String(doc.last_refreshed_at).slice(0, 16).replace('T', ' ')} UTC` : '';
  const upcoming = nextRefresh(doc.schedule, new Date());
  const nextText = upcoming ? ` · next ${utcStamp(upcoming)}` : '';
  const value = (s: ReviewSection, sx: Record<string, any>) => (
    <ClaimValue claim={refreshedClaim(s)} artifact={{ kind: 'review', thread_id: threadId, section_id: s.id }} sx={sx}>
      {formatKpiValue(s.value ?? s.claim?.value, s.claim?.format)}
    </ClaimValue>
  );
  /**
   * The commentary Stroma writes (#568), labelled as such and never edited:
   * each significant move with the walk's verdict and its evidence tier, and
   * what the deck check found — every value a claim, every line with its source.
   */
  const stromaSummary = () => {
    const check = doc.deck_check;
    const moved = pinned.filter((s) => s.kind === 'claim' && s.flag === 'significant' && s.why?.verdict?.headline);
    const deckRows = (check?.rows || []).map((row) => ({ row, section: sectionForDeckRow(thread, row) }))
      .filter(({ row, section }) => ['stale', 'definition'].includes(currentResult(row, section, check)) && !!(section || row.claim));
    return (
      <Box data-testid="review-stroma-summary" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'var(--sunk)', p: 1.5, display: 'grid', gap: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Written by Stroma · from this week's walks and the deck check</Typography>
        {moved.length === 0 && deckRows.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Nothing moved outside its normal range at the last refresh, and the deck check has nothing to settle.</Typography>
        )}
        {moved.map((s) => {
          const opens = opensAs(savedThreads, s);
          const session = s.why?.session_id || s.source?.session_id;
          return (
            <Box key={s.id} data-testid="review-stroma-line" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', fontSize: 13.5 }}>
              {s.why?.driver?.tier && <TierChip tier={s.why.driver.tier} />}
              <Box component="span" sx={{ flex: 1, minWidth: 200 }}>
                <b>{sectionTitle(s)}</b> {value(s, { fontWeight: 700 })} {moveText(s) || ''} — {s.why!.verdict!.headline}
              </Box>
              {opens ? (
                <Button size="small" onClick={() => openSection(s)} data-testid="review-stroma-source">Source →</Button>
              ) : traceLinksOn() && session ? (
                <a href={traceUrl({ session })} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-ink)' }} data-testid="review-stroma-source">Source →</a>
              ) : null}
            </Box>
          );
        })}
        {deckRows.map(({ row, section }) => {
          // every value here is a claim: the deck's number, which reproduced, opens as the grading's claim for it.
          // The warehouse's other readings — the fact table's real value, what
          // the label alone reads — are not claims of this review; the deck check's table carries them.
          // the deck's number as graded: the grading's own claim carries that value (a refreshed section carries this week's)
          const claimed = row.claim || (section ? refreshedClaim(section) : null);
          const deckValue = claimed
            ? <ClaimValue claim={claimed} artifact={!row.claim && section ? { kind: 'review', thread_id: threadId, section_id: section.id } : undefined} sx={{ fontWeight: 700 }}>{row.deck}</ClaimValue>
            : null;
          return (
          <Box key={`deck-${row.id}`} data-testid="review-stroma-line" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', fontSize: 13.5 }}>
            <Box component="span" sx={{ flex: 1, minWidth: 200 }}>
              {row.verdict === 'stale'
                ? <><b>{row.label}</b>: the deck's {deckValue} is stale — built before the data finished loading; the real value is in the deck check ({deckName(check)})</>
                : <><b>{row.label}</b>: the deck's {deckValue} reads {row.resolved?.perspective_name || row.resolved?.metric}'s definition — settle it in the deck check</>}
            </Box>
            {(section || row.claim) && (
              <Button size="small" data-testid="review-stroma-source"
                onClick={(e) => (section ? popover.open(refreshedClaim(section), e.currentTarget, null, { kind: 'review', thread_id: threadId, section_id: section.id }) : popover.open(row.claim, e.currentTarget))}>
                Source →
              </Button>
            )}
          </Box>
          );
        })}
      </Box>
    );
  };
  const saveSchedule = async (v: ReviewSchedule | null) => {
    const ok = await schedule(threadId, v);
    if (ok) setScheduleAnchor(null);
  };

  return (
    <Box data-testid="review-doc" sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* the title line: rename in place · stage · Refresh now · trace · Reschedule · ★ · ✕ */}
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {renaming ? (
                <TextField autoFocus size="small" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} inputProps={{ 'aria-label': 'Review title', maxLength: 200 }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitRename(); } if (e.key === 'Escape') setRenaming(false); }}
                  onBlur={commitRename} sx={{ minWidth: 320 }} data-testid="review-rename" />
              ) : (
                <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', cursor: 'text', '&:hover': { textDecoration: 'underline dotted' } }}
                  role="button" tabIndex={0} title="Click to rename this review" data-testid="review-title"
                  onClick={() => { setDraftTitle(thread.title); setRenaming(true); }} onKeyDown={(e) => { if (e.key === 'Enter') { setDraftTitle(thread.title); setRenaming(true); } }}>
                  {thread.title}
                </Typography>
              )}
              <StatusChip status={stage === 'live' ? 'go' : 'draft'} size="small" label={stage === 'live' ? 'Live' : 'Draft'} data-testid="review-stage" sx={{ height: 20, fontSize: '0.68rem' }} />
              <Button size="small" variant="outlined" disabled={!!busy} onClick={() => refresh(threadId)} data-testid="review-refresh" sx={{ ml: 0.5 }}>
                {busy === 'refreshing' ? 'Refreshing…' : 'Refresh now'}
              </Button>
              {doc.last_refresh_session_id && <TraceLink sessionId={doc.last_refresh_session_id} title="Open the last refresh's trace — every number re-derived, every walk" />}
              <Button size="small" variant={doc.schedule ? 'text' : 'contained'} disabled={!!busy} onClick={(e) => setScheduleAnchor(e.currentTarget)} data-testid="review-schedule">
                {doc.schedule ? 'Reschedule' : 'Schedule & go live'}
              </Button>
              <Popover open={!!scheduleAnchor} anchorEl={scheduleAnchor} onClose={() => setScheduleAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box data-testid="review-schedule-form" sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, flexWrap: 'wrap', maxWidth: 420 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Refresh every</Typography>
                  <Select size="small" value={weekday} onChange={(e) => setWeekday(String(e.target.value))} inputProps={{ 'data-testid': 'review-weekday' }} sx={{ minWidth: 130 }}>
                    {WEEKDAYS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </Select>
                  <Typography sx={{ fontSize: 13 }}>at</Typography>
                  <TextField size="small" value={at} onChange={(e) => setAt(e.target.value)} inputProps={{ 'data-testid': 'review-at', 'aria-label': 'Time (UTC)', style: { width: 64 } }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>UTC</Typography>
                  <Box sx={{ flex: 1 }} />
                  {doc.schedule && <Button size="small" onClick={() => saveSchedule(null)}>Stop refreshing</Button>}
                  <Button size="small" variant="contained" disabled={busy === 'scheduling'} onClick={() => saveSchedule({ weekday, at })} data-testid="review-schedule-save">
                    {stage === 'live' ? 'Save' : 'Go live'}
                  </Button>
                  {/* the popover is modal: an error behind it is out of reach, so it is said here */}
                  {error && <Alert severity="error" sx={{ width: '100%' }} onClose={() => useVoyagerStore.setState({ reviewsError: null } as any)} data-testid="review-schedule-error">{error}</Alert>}
                </Box>
              </Popover>
              <Box sx={{ flex: 1 }} />
              <SendToExec />
              <ShareButton threadId={thread.id} />
              <Tooltip title={thread.starred ? 'Unstar' : 'Star this review'}>
                <IconButton size="small" aria-label={thread.starred ? 'Unstar thread' : 'Star thread'} onClick={() => starThread(thread.id, !thread.starred)} sx={{ color: thread.starred ? 'warning.main' : 'text.disabled' }}>
                  {thread.starred ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close — it moves to History; a scheduled review keeps refreshing">
                <IconButton size="small" aria-label="Close thread" onClick={() => closeThread(thread.id)} sx={{ color: 'text.disabled' }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="review-provenance-line">
              {stage} · grounded on <b>{perspectiveName}</b> · {scheduleText} · {describeWindowRule(doc.window_rule)}{refreshedText}{nextText} · from the {doc.template_name || doc.template_id || 'template'} template
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Button size="small" disabled={!!busy} data-testid="review-window-rule" onClick={(e) => { setDraftRule(doc.window_rule || null); setRuleAnchor(e.currentTarget); }}>Change window rule</Button>
            <Popover open={!!ruleAnchor} anchorEl={ruleAnchor} onClose={() => setRuleAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <Box sx={{ p: 2, display: 'grid', gap: 1.5, maxWidth: 520 }}>
                <ReviewWindowRulePicker value={draftRule} onChange={setDraftRule} allowLegacy />
                <Typography variant="caption" color="text.secondary">Applies on the next refresh. Frozen returns to each number's source artifact period.</Typography>
                <Button size="small" variant="contained" data-testid="review-window-rule-save"
                  disabled={!!busy || (draftRule?.kind === 'trailing_days' && (!Number.isSafeInteger(draftRule.days) || draftRule.days < 1))}
                  onClick={async () => { if (await saveWindowRule(threadId, draftRule)) setRuleAnchor(null); }}>Save window rule</Button>
                {error && <Alert severity="error">{error}</Alert>}
              </Box>
            </Popover>
          </Box>
          {error && !scheduleAnchor && <Alert severity="error" onClose={() => useVoyagerStore.setState({ reviewsError: null } as any)} data-testid="review-error">{error}</Alert>}
          <DeckCheck threadId={threadId} />
          <Section title="Since last Monday" testId="review-since">
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.25, fontSize: 13.5, color: 'var(--ink-2)' }}>
              {sinceLastMonday(thread, new Date()).map((line, i) => <li key={i} data-testid="review-since-line">{line}</li>)}
            </Box>
          </Section>

          {/* the KPI tiles: a ⚡ where a number moved; a tile's name opens its details in the side panel */}
          {kpiSlot && (() => {
            const groups = tileGroups(kpiSlot, tiles);
            const titles = groups.map((g) => g.title);
            const single = groups.length === 1;
            return (
              <Box data-testid="review-kpis" sx={{ display: 'grid', gap: 2.5 }}>
                {groups.map((g, gi) => {
                  // one scorecard pads its row to four; a grouped one ends each group with one place to pin into
                  const empties = single ? Math.max(0, 4 - g.tiles.length) : (g.tiles.length < 4 ? 1 : 0);
                  return (
                    <Box key={g.title} data-testid="review-kpi-group" sx={{ display: 'grid', gap: 1.25 }}>
                      {!single && <Typography component="h2" sx={{ fontFamily: 'var(--display)', fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', m: 0 }}>{g.title}</Typography>}
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
                        {g.tiles.map((s) => (
                          <Box key={s.id} sx={{ display: 'grid' }}>
                            <ReviewTile threadId={threadId} section={s} reviewTitle={thread.title} groups={titles} notes={notes[s.id] || 0} />
                            {findingOf(s) && (
                              <Typography variant="caption" noWrap data-testid="review-tile-finding" title={findingOf(s)} sx={{ color: 'text.secondary', fontSize: 11, px: 0.5, mt: 0.25 }}>{findingOf(s)}</Typography>
                            )}
                          </Box>
                        ))}
                        {Array.from({ length: empties }).map((_, i) => (
                          <Box key={`empty-${i}`} data-testid="review-tile-empty" sx={{ minHeight: 96, p: 1.5, border: '1.5px dashed', borderColor: 'divider', borderRadius: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', fontSize: 12.5, textAlign: 'center' }}>
                            {gi === 0 && i === 0 && tiles.length === 0 ? 'Pin numbers from any thread' : 'Pick a metric'}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
          {findings.length > 0 && (
            <Section title="What moved and why" testId="review-findings">
              <Box sx={{ display: 'grid', gap: 1 }}>
                {findings.map((s) => (
                  <Box key={s.id} data-testid="review-finding" sx={{ display: 'grid', gap: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                      {opensAs(savedThreads, s) ? (
                        <Typography component="span" role="button" tabIndex={0} data-testid="review-finding-open"
                          onClick={() => openSection(s)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSection(s); } }}
                          title="Open this number's analysis" sx={{ fontSize: 13, fontWeight: 700, cursor: 'pointer', '&:hover': { color: 'primary.dark' } }}>
                          {sectionTitle(s)}
                        </Typography>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{sectionTitle(s)}</Typography>
                      )}
                      {value(s, { fontSize: 13, fontWeight: 700, color: 'primary.dark', fontVariantNumeric: 'tabular-nums' })}
                      <WhyChip s={s} />
                      {s.why?.driver?.tier && <TierChip tier={s.why.driver.tier} />}
                      <SupportRef session={s.why?.session_id || s.source?.session_id} />
                      {traceLinksOn() && (s.why?.session_id || s.source?.session_id) && (
                        <a href={traceUrl({ session: s.why?.session_id || s.source?.session_id })} target="_blank" rel="noopener noreferrer" data-testid="review-finding-trace"
                          style={{ fontSize: 11.5, color: 'inherit' }} title="the walk behind this finding">trace</a>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{findingOf(s)}</Typography>
                  </Box>
                ))}
              </Box>
            </Section>
          )}

          {tableSlot && rows.length > 0 && (
            <Section title={tableSlot.title || 'Performance by segment'} testId="review-table">
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper', overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, '& td, & th': { px: 1.75, py: 0.75, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }, '& thead th': { borderTop: 0, color: 'text.secondary', fontWeight: 600, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                  <thead><tr><th>Number</th><th style={{ textAlign: 'right' }}>Value</th><th style={{ textAlign: 'right' }}>Previous</th><th style={{ textAlign: 'right' }}>Change</th><th>Pinned from</th></tr></thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.id} data-testid="review-table-row" style={{ background: s.flag ? 'var(--warn-tint)' : undefined }}>
                        <td>{sectionTitle(s)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value(s, {})}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.previous != null ? formatKpiValue(s.previous, s.claim?.format) : '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', color: (s.delta || 0) >= 0 ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }}>
                            {deltaText(s.delta, s.previous) || '—'}<WhyChip s={s} />
                          </Box>
                        </td>
                        <td style={{ color: 'var(--ink-2)', fontSize: 12 }}>{s.source?.ref || s.source?.thread_title || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            </Section>
          )}

          {commentarySlot && (
            <Section title={commentarySlot.title || 'Commentary'} testId="review-commentary">
              <TextField
                multiline
                minRows={3}
                size="small"
                placeholder="What the numbers above mean this week. Every claim links to its query."
                value={commentary}
                onChange={(e) => setCommentaryText(e.target.value)}
                onBlur={() => { if (commentary !== (doc.commentary || '')) setCommentary(threadId, commentary); }}
                inputProps={{ 'aria-label': 'Review commentary', style: { fontSize: 13, lineHeight: 1.55 } }}
                sx={{ bgcolor: 'background.paper' }}
              />
              {stromaSummary()}
            </Section>
          )}

          {actions.length > 0 && (
            <Section title={actionsSlot?.title || 'Action items & next steps'} testId="review-actions">
              <Box sx={{ display: 'grid', px: 1.75, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
                {actions.map((s) => <ReviewSectionRow key={s.id} threadId={threadId} section={s} />)}
              </Box>
            </Section>
          )}

          <Section title="Pinned this week" hint="Confirming a number confirms the number; the cause is confirmed separately, on the verdict." testId="review-pinned-section">
            {pinned.length === 0 ? (
              <Box data-testid="review-pins-empty" sx={{ p: 2, border: '1.5px dashed', borderColor: 'divider', borderRadius: 2.5, color: 'text.disabled', fontSize: 13, textAlign: 'center', lineHeight: 1.6 }}>
                Nothing pinned yet — run an analysis in any tab and pin it here.<br />It will refresh with the review, why-walk and all.
              </Box>
            ) : (
              <Box sx={{ display: 'grid', px: 1.75, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }} data-testid="review-pinned-list">
                {/* the tiles above are the scalar numbers; the list keeps what is not a tile — a row's number, a number with no scorecard slot */}
                {others.map((s) => <ReviewSectionRow key={s.id} threadId={threadId} section={s} />)}
                {others.length === 0 && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', py: 1 }} data-testid="review-pinned-all-tiles">Every pinned number is a tile above — open one for where it came from and its analyses.</Typography>
                )}
              </Box>
            )}
          </Section>
        </Box>
      </Box>
      <Box sx={{ width: 260, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider', p: 2, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', gap: 1.5, overflow: 'auto' }} data-testid="review-rail">
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Ask this review</Typography>
          <AskThisReview thread={thread} />
        </Box>
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }} data-testid="review-activity">
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Activity</Typography>
          {(doc.activity || []).length === 0 && <Typography variant="caption" sx={{ color: 'text.disabled' }}>Nothing yet — every start, pin, schedule, refresh and rename lands here, each with its trace.</Typography>}
          {[...(doc.activity || [])].reverse().slice(0, 8).map((a, i) => (
            <Box key={`${a.at}-${i}`} sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', py: 0.25 }} data-testid="review-activity-row">
              <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, minWidth: 0 }}>
                <b>{a.action}</b>{a.detail?.label ? ` · ${a.detail.label}` : a.detail?.schedule ? ` · ${a.detail.schedule}` : a.detail?.to ? ` · “${a.detail.to}”` : ''}{a.by ? ` · ${a.by}` : ''}{a.at ? ` · ${String(a.at).slice(0, 10)}` : ''}
              </Typography>
              <SupportRef session={a.session_id} />
              {traceLinksOn() && a.session_id && (
                <a href={traceUrl({ session: a.session_id })} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'inherit' }} data-testid="review-activity-trace">trace</a>
              )}
            </Box>
          ))}
          {traceLinksOn() && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              <a href={traceUrl({ thread: threadId })} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }} data-testid="review-trace-all">All traces for this review →</a>
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2.5, bgcolor: 'background.paper' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>How this document works</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Numbers are never authored here — they're <b>pinned</b> from analysis tabs and re-derived on the schedule from their own grounding; a <b>⚡</b> marks a significant move the engine has already walked. Commentary is the one part you write.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
